///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

/* JS implementation of the strcmp95 C function written by
Bill Winkler, George McLaughlin, Matt Jaro and Maureen Lynch,
released in 1994 (http://web.archive.org/web/20100227020019/http://www.census.gov/geo/msb/stand/strcmp.c).
a and b should be strings. Always performs case-insensitive comparisons
and always adjusts for long strings. */

import { Injectable } from '@angular/core';

@Injectable({
    providedIn: 'root'
})
// Implementation of Jaro-Winkler distance algorithm
export class StringDistance {
    // The char adjustment table used below
    public adjustments: any = {
        A: 'U',
        B: 'V',
        O: 'U',
        C: 'G',
        E: 'F',
        W: 'V',
        S: 'Z',
        X: 'S',
        U: 'V',
        M: 'N',
        L: 'I',
        Q: 'O',
        P: 'R',
        I: 'J',
        '2': 'Z',
        '5': 'S',
        '8': 'B',
        '1': 'L',
        '0': 'Q',
        G: 'J',
        Y: ' '
    };

    public getDistance(a: string, b: string): number {
        if (!a || !b) return 0.0;

        a = a.trim().toUpperCase();
        b = b.trim().toUpperCase();

        const a_flag = new Array(a.length).fill(0);
        const b_flag = new Array(b.length).fill(0);

        const searchRange = Math.floor(Math.max(a.length, b.length) / 2) - 1;
        const minValue = Math.min(a.length, b.length);

        const Num_com = this.findMatchingCharacters(a, b, a_flag, b_flag, searchRange);

        if (Num_com === 0) return 0.0;

        const N_trans = this.countTranspositions(a, b, a_flag, b_flag);
        const Num_sim = this.adjustForSimilarities(a, b, a_flag, b_flag, Num_com, minValue);

        let weight = (Num_sim / a.length + Num_sim / b.length + (Num_com - N_trans) / Num_com) / 3;
        weight = this.applyFinalAdjustments(weight, a, b, minValue, Num_com);

        return weight;
    }

    private findMatchingCharacters(a: string, b: string, a_flag: number[], b_flag: number[], searchRange: number): number {
        let numCommon = 0;
        for (let i = 0; i < a.length; i++) {
            const start = Math.max(0, i - searchRange);
            const end = Math.min(i + searchRange, b.length - 1);
            for (let j = start; j <= end; j++) {
                if (a[j] === b[i] && b_flag[i] !== 1 && a_flag[j] !== 1) {
                    a_flag[j] = b_flag[i] = 1;
                    numCommon++;
                    break;
                }
            }
        }
        return numCommon;
    }

    private countTranspositions(a: string, b: string, aMatches: number[], bMatches: number[]): number {
        let transpositions = 0;
        let searchStartIndex = 0;

        for (let i = 0; i < a.length; i++) {
            if (aMatches[i] === 1) {
                const matchedIndexInB = bMatches.findIndex((match, index) => match === 1 && index >= searchStartIndex);

                if (matchedIndexInB !== -1) {
                    searchStartIndex = matchedIndexInB + 1;
                    if (a[i] !== b[matchedIndexInB]) {
                        transpositions++;
                    }
                }
            }
        }

        return Math.floor(transpositions / 2);
    }

    private adjustForSimilarities(a: string, b: string, aFlag: number[], bFlag: number[], numCommon: number, minValue: number): number {
        let similarityScore = 0;

        if (minValue > numCommon) {
            a.split('').forEach((charA, i) => {
                if (!aFlag[i]) {
                    const indexB = b.split('').findIndex((charB, j) => !bFlag[j] && this.adjustments[charA] === charB);
                    if (indexB !== -1) {
                        similarityScore += 3;
                        bFlag[indexB] = 2;
                    }
                }
            });
        }

        return similarityScore / 10.0 + numCommon;
    }

    private applyFinalAdjustments(weight: number, a: string, b: string, minv: number, Num_com: number): number {
        if (weight > 0.7) {
            const j = minv >= 4 ? 4 : minv;
            let i = 0;
            while (i < j && a[i] === b[i]) {
                i++;
            }
            if (i) {
                weight += i * 0.1 * (1.0 - weight);
            }

            if (minv > 4 && Num_com > i + 1 && 2 * Num_com >= minv + i) {
                weight += (1.0 - weight) * ((Num_com - i - 1) / (a.length * b.length - i * 2 + 2));
            }
        }
        return weight;
    }
}
